import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../Providers/LanguageContext';
import styles from './CookiePolicy.module.css';
import { playUISound } from '../../utils/UISoundEffects';

export function CookiePolicy() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <div className={styles.cookieContainer}>
      <button
        className={styles.backButton}
        onClick={() => { playUISound(); navigate(-1); }}
        aria-label={t('common.back')}
      >
        ← {t('common.back')}
      </button>
      <div className={styles.header}>
        <h1>{t('cookiePolicy.title')}</h1>
        <p className={styles.lastUpdated}>
          {t('cookiePolicy.lastUpdated')}: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      <div className={styles.content}>
        {/* Introduction */}
        <section className={styles.section}>
          <h2>{t('cookiePolicy.introduction.title')}</h2>
          <p>{t('cookiePolicy.introduction.p1')}</p>
          <p>{t('cookiePolicy.introduction.p2')}</p>
        </section>

        {/* What Are Cookies */}
        <section className={styles.section}>
          <h2>{t('cookiePolicy.what.title')}</h2>
          <p>{t('cookiePolicy.what.p1')}</p>
          <p>{t('cookiePolicy.what.p2')}</p>
        </section>

        {/* Types of Cookies */}
        <section className={styles.section}>
          <h2>{t('cookiePolicy.types.title')}</h2>

          <h3>{t('cookiePolicy.types.necessary.title')}</h3>
          <p>{t('cookiePolicy.types.necessary.description')}</p>
          <p><strong>{t('cookiePolicy.types.necessary.examples')}</strong></p>
          <ul>
            <li>{t('cookiePolicy.types.necessary.item1')}</li>
            <li>{t('cookiePolicy.types.necessary.item2')}</li>
            <li>{t('cookiePolicy.types.necessary.item3')}</li>
            <li>{t('cookiePolicy.types.necessary.item4')}</li>
          </ul>

          <h3>{t('cookiePolicy.types.analytics.title')}</h3>
          <p>{t('cookiePolicy.types.analytics.description')}</p>
          <p><em>{t('cookiePolicy.types.analytics.provider')}</em></p>
          <p><strong>{t('cookiePolicy.types.analytics.purpose')}</strong></p>
          <ul>
            <li>{t('cookiePolicy.types.analytics.item1')}</li>
            <li>{t('cookiePolicy.types.analytics.item2')}</li>
            <li>{t('cookiePolicy.types.analytics.item3')}</li>
            <li>{t('cookiePolicy.types.analytics.item4')}</li>
          </ul>

          <h3>{t('cookiePolicy.types.advertising.title')}</h3>
          <p>{t('cookiePolicy.types.advertising.description')}</p>
          <p><em>{t('cookiePolicy.types.advertising.provider')}</em></p>
          <p><strong>{t('cookiePolicy.types.advertising.purpose')}</strong></p>
          <ul>
            <li>{t('cookiePolicy.types.advertising.item1')}</li>
            <li>{t('cookiePolicy.types.advertising.item2')}</li>
            <li>{t('cookiePolicy.types.advertising.item3')}</li>
          </ul>
          <p className={styles.note}>{t('cookiePolicy.types.advertising.note')}</p>
        </section>

        {/* First vs Third-Party */}
        <section className={styles.section}>
          <h2>{t('cookiePolicy.firstThird.title')}</h2>

          <h3>{t('cookiePolicy.firstThird.firstParty.title')}</h3>
          <p>{t('cookiePolicy.firstThird.firstParty.description')}</p>

          <h3>{t('cookiePolicy.firstThird.thirdParty.title')}</h3>
          <p>{t('cookiePolicy.firstThird.thirdParty.description')}</p>
        </section>

        {/* Specific Cookies */}
        <section className={styles.section}>
          <h2>{t('cookiePolicy.specific.title')}</h2>
          <p>{t('cookiePolicy.specific.tableHeader')}</p>

          <div className={styles.table}>
            <table>
              <thead>
                <tr>
                  <th>{t('cookiePolicy.cookieTable.name')}</th>
                  <th>{t('cookiePolicy.cookieTable.purpose')}</th>
                  <th>{t('cookiePolicy.cookieTable.duration')}</th>
                  <th>{t('cookiePolicy.cookieTable.type')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>zodiac_cookie_consent</td>
                  <td>Stores your cookie preferences</td>
                  <td>1 year</td>
                  <td>Necessary</td>
                </tr>
                <tr>
                  <td>zodiac_language</td>
                  <td>Remembers your language preference</td>
                  <td>1 year</td>
                  <td>Necessary</td>
                </tr>
                <tr>
                  <td>zodiac_theme</td>
                  <td>Remembers light/dark mode preference</td>
                  <td>1 year</td>
                  <td>Necessary</td>
                </tr>
                <tr>
                  <td>_ga, _gid</td>
                  <td>Google Analytics - tracks visitor interactions</td>
                  <td>2 years / 24 hours</td>
                  <td>Analytics</td>
                </tr>
                <tr>
                  <td>_gat</td>
                  <td>Google Analytics - throttles request rate</td>
                  <td>1 minute</td>
                  <td>Analytics</td>
                </tr>
                <tr>
                  <td>__gads, __gac</td>
                  <td>Google AdSense - ad delivery and measurement</td>
                  <td>13 months</td>
                  <td>Advertising</td>
                </tr>
                <tr>
                  <td>IDE, ANID</td>
                  <td>DoubleClick - ad personalization</td>
                  <td>13 months</td>
                  <td>Advertising</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* How to Control */}
        <section className={styles.section}>
          <h2>{t('cookiePolicy.control.title')}</h2>

          <h3>{t('cookiePolicy.control.consent.title')}</h3>
          <p>{t('cookiePolicy.control.consent.description')}</p>
          <ul>
            <li>{t('cookiePolicy.control.consent.item1')}</li>
            <li>{t('cookiePolicy.control.consent.item2')}</li>
            <li>{t('cookiePolicy.control.consent.item3')}</li>
          </ul>

          <h3>{t('cookiePolicy.control.browser.title')}</h3>
          <p>{t('cookiePolicy.control.browser.description')}</p>
          <ul>
            <li>{t('cookiePolicy.control.browser.item1')}</li>
            <li>{t('cookiePolicy.control.browser.item2')}</li>
            <li>{t('cookiePolicy.control.browser.item3')}</li>
            <li>{t('cookiePolicy.control.browser.item4')}</li>
          </ul>
          <p>{t('cookiePolicy.control.browser.links')}</p>
          <ul>
            <li><strong>Chrome:</strong> {t('cookiePolicy.browserInstructions.chrome')}</li>
            <li><strong>Firefox:</strong> {t('cookiePolicy.browserInstructions.firefox')}</li>
            <li><strong>Safari:</strong> {t('cookiePolicy.browserInstructions.safari')}</li>
            <li><strong>Edge:</strong> {t('cookiePolicy.browserInstructions.edge')}</li>
          </ul>

          <h3>{t('cookiePolicy.control.optOut.title')}</h3>
          <p>{t('cookiePolicy.control.optOut.description')}</p>
          <ul>
            <li>
              <a
                href="https://www.google.com/settings/ads"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                {t('cookiePolicy.control.optOut.googleLink')}
              </a>
              {t('cookiePolicy.control.optOut.googleDesc')}
            </li>
            <li>
              <a
                href="https://optout.aboutads.info/"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                {t('cookiePolicy.control.optOut.daaLink')}
              </a>
              {t('cookiePolicy.control.optOut.daaDesc')}
            </li>
            <li>
              <a
                href="https://www.youronlinechoices.com/"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                {t('cookiePolicy.control.optOut.edaaLink')}
              </a>
              {t('cookiePolicy.control.optOut.edaaDesc')}
            </li>
          </ul>
        </section>

        {/* Impact of Disabling */}
        <section className={styles.section}>
          <h2>{t('cookiePolicy.impact.title')}</h2>
          <p>{t('cookiePolicy.impact.p1')}</p>
          <ul>
            <li>{t('cookiePolicy.impact.item1')}</li>
            <li>{t('cookiePolicy.impact.item2')}</li>
            <li>{t('cookiePolicy.impact.item3')}</li>
            <li>{t('cookiePolicy.impact.item4')}</li>
          </ul>
        </section>

        {/* Updates */}
        <section className={styles.section}>
          <h2>{t('cookiePolicy.updates.title')}</h2>
          <p>{t('cookiePolicy.updates.p1')}</p>
          <p>{t('cookiePolicy.updates.p2')}</p>
        </section>

        {/* Contact */}
        <section className={styles.section}>
          <h2>{t('cookiePolicy.contact.title')}</h2>
          <p>{t('cookiePolicy.contact.p1')}</p>
          <p>
            <strong>{t('cookiePolicy.contact.email')}</strong> {t('common.contactEmail')}
          </p>
          <p>
            <strong>{t('cookiePolicy.contact.company')}</strong> {t('common.companyName')}
            <br />
            {t('common.companyCountry')}
          </p>
        </section>

        {/* Related Policies */}
        <section className={styles.section}>
          <h2>{t('cookiePolicy.relatedPolicies.title')}</h2>
          <p>{t('cookiePolicy.relatedPolicies.description')}</p>
          <ul className={styles.linksList}>
            <li>
              <a href="/privacy" className={styles.link}>
                {t('cookiePolicy.relatedPolicies.privacy')}
              </a>
            </li>
            <li>
              <a href="/terms" className={styles.link}>
                {t('cookiePolicy.relatedPolicies.terms')}
              </a>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}
